import Icon from "@/components/Icon";
import {
  Box,
  Center,
  chakra,
  Flex,
  FlexProps,
  Image,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Spacer,
  Text,
} from "@chakra-ui/react";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { getNoliboxSmallModels } from "../../request/model";
import { ReactComponent as ArrowSvg } from "@/assets/icons/arrow-down.svg";
import { genBlock } from "@/utils/bem";
import { KeywordList } from "../WordTable/KeywordList";
import {
  TableForSearchItem,
  WordEntityTypeWithParentKeys,
  WordNameDictionary,
  WordTableTypeWithParentKeys,
} from "../WordTable/type";
import { WordTableContext } from "../WordTable/context";
import { Dictionary, WithClassName } from "@/types/base";
import { Editor_Words } from "@/utils/lang/editor";
import { formatTableData, getKeyValueMap, getTableForSearch } from "../WordTable/utils";
import { observer } from "mobx-react-lite";
import { langStore } from "@noli/business";
import { EllipseText } from "../styleComponents/Texts";
import { UsageOverlay } from "../WordTable/KeywordItemCard";

const searchFilter = (list: TableForSearchItem[], names: WordNameDictionary) => {
  return (value = "") => {
    const resultSet = new Set();
    const result = list.filter((item) => {
      if (resultSet.has(item.key)) return false;
      resultSet.add(item.key);
      return (
        names[item.key].en.toLowerCase().includes(value.toLowerCase()) ||
        names[item.key].zh.includes(value) ||
        item.value.toLowerCase().includes(value.toLowerCase()) ||
        item.labels?.some((label) => label.includes(value))
      );
    });
    return result;
  };
};

const block = genBlock("c-small-models-selector");
interface WordTableProps {
  type?: 1 | 2; // 1: 画宇宙模型 2: 画宇宙动漫模型
  value: string[];
  onChange: (value: string) => void;
}
const _SmallModelsSelector: React.FC<WithClassName<WordTableProps>> = ({
  type = 1,
  className,
  value,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const [dataset, setDataset] = useState<{
    data: WordTableTypeWithParentKeys;
    searchList: WordEntityTypeWithParentKeys[];
    keyValueMap: Dictionary<any>;
    nameDict: WordNameDictionary;
  }>({
    data: [],
    searchList: [],
    keyValueMap: {},
    nameDict: {},
  });
  const { nameDict } = dataset;
  const lang = langStore.tgt;

  useEffect(() => {
    getNoliboxSmallModels(type)
      .then((res) => {
        return res;
      })
      .then((res) => {
        const { data } = res;
        const formatAsWordTableData: WordTableTypeWithParentKeys = [
          { children: data.data, key: "root" },
        ];
        const dataWithParentKeys = formatTableData(formatAsWordTableData);
        const listForSearch = getTableForSearch(formatAsWordTableData);
        const keyValueMap = getKeyValueMap(formatAsWordTableData);
        setDataset({
          data: dataWithParentKeys,
          searchList: listForSearch,
          keyValueMap,
          nameDict: data.dictionary,
        });
      });
  }, [type]);

  function handleSelect(word: WordEntityTypeWithParentKeys) {
    // onSelect?.(word.value);
    onChange(word.key);
  }

  const checkValueFn = (itemValue: string) => value.includes(itemValue);

  const selectedText = useMemo(() => {
    if (!value?.length) {
      return "暂无";
    }
    return value
      .map((key) => nameDict?.[key]?.[lang])
      .filter((name) => !!name)
      ?.join(", ");
  }, [value, nameDict]);
  return (
    <Box className={className} mt="16px">
      <WordTableContext.Provider
        value={{
          onSelect: handleSelect,
          nameDict,
          checkValueFn: (item) => checkValueFn(item.value),
          tableRightSide: <Text fontSize="13px">小模型可与画宇宙/动漫模型效果叠加</Text>,
          TableItemRender: IModelItemCard,
          keywordListConfig: {
            containerMaxWidth: "724px",
            containerWidth: "724px",
            columnCount: 3,
          },
          searchFilter,
        }}
      >
        <Popover
          isLazy
          placement="right"
          boundary={document.body}
          matchWidth
          isOpen={open}
          onClose={() => setOpen(false)}
          preventOverflow
          flip
          gutter={25}
          modifiers={[
            {
              name: "topLogger",
              enabled: true,
              phase: "main",
              fn: ({ state }) => {
                state.styles.popper.zIndex = "1000";
                return state;
              },
            },
          ]}
        >
          <PopoverTrigger>
            <Flex
              minH="24px"
              color="text.title"
              fontSize="14px"
              align="center"
              cursor="pointer"
              tabIndex={0}
              onClick={() => setOpen((open) => !open)}
              _hover={{
                color: "brand.primary",
              }}
            >
              <Text>小模型 : {selectedText}</Text>
              <Spacer />
              <Icon
                className={block({ e: "arrow_icon" })}
                svg={ArrowSvg}
                fontSize="24px"
                padding="6px"
                transform="rotate(-90deg)"
                fillbyCurrentColor
                flexShrink={0}
              />
            </Flex>
          </PopoverTrigger>
          <PopoverContent border="none" bg="#3A3A3A" rounded="12px" w="auto" zIndex={500}>
            <PopoverBody p="0px" w="fit-content">
              {dataset.data?.[0]?.children && (
                <KeywordList list={dataset.data?.[0]?.children} onClose={() => setOpen(false)} />
              )}
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </WordTableContext.Provider>
    </Box>
  );
};

export const SmallModelsSelector = chakra(observer(_SmallModelsSelector));

/**
 * 关键词选项
 */
const IModelItemCard: React.FC<{
  data: WordEntityTypeWithParentKeys;
}> = ({ data }) => {
  const { onSelect, nameDict, checkValueFn } = useContext(WordTableContext);
  const lang = langStore.tgt;

  const isChecked = checkValueFn ? checkValueFn(data) : false;
  const imgUrl = data.image_urls;

  return (
    <Flex
      flexDir="column"
      border="1px solid"
      borderColor={isChecked ? "brand.primary" : "text.primary"}
      h="100%"
      alignItems="center"
      rounded="6px"
      overflow="hidden"
      cursor="pointer"
      fontSize="12px"
      lineHeight="16px"
      bg="bg.panelBg"
      p="4px"
      w="100%"
      onClick={() => onSelect(data)}
    >
      {imgUrl ? (
        <Box pos="relative" w="100%" flex={1}>
          <Image
            src={`${imgUrl}?x-oss-process=image/resize,w_250`}
            w="100%"
            h="100%"
            objectFit="cover"
            rounded="4px"
          />
        </Box>
      ) : null}
      <Flex w="100%" mt="8px" p="0px 12px 4px" justifyContent="space-between">
        <Center w="calc(100% - 54px)" flexDirection="column" h="48px" mr="4px">
          {lang === "zh" && nameDict[data.key]?.zh && (
            <EllipseText w="100%" color="text.secondary" mb="4px">
              <span title={nameDict[data.key]?.zh}>{nameDict[data.key]?.zh}</span>
            </EllipseText>
          )}
          <EllipseText w="100%" color="text.secondary" onClick={() => onSelect(data)}>
            <span title={nameDict[data.key]?.en}>{nameDict[data.key]?.en}</span>
          </EllipseText>
        </Center>

        <Box flexShrink={0} maxW="54px">
          <UsageOverlay isChecked={isChecked} />
        </Box>
      </Flex>
    </Flex>
  );
};
